import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { Capacitor } from '@capacitor/core';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import {
  User,
  Eye,
  EyeOff,
  Shield,
  CreditCard,
  Calendar,
  Lightbulb,
  HelpCircle,
  MessageCircle,
  LogOut,
  ChevronRight,
  ExternalLink,
  Bell,
  PartyPopper,
  Gift,
  Trash2,
  FileText,
  ShieldCheck,
  Activity,
} from 'lucide-react';
import RoleSwitcher from './RoleSwitcher';

// Navigation row component - chevron always means navigate
const NavRow = ({ icon: Icon, label, description, onClick, badge, badgeVariant = 'secondary', danger = false, 'data-testid': dataTestId }) => (
  <button
    onClick={onClick}
    data-testid={dataTestId}
    className={`w-full flex items-center justify-between p-4 hover:bg-gray-50 active:bg-gray-100 transition-colors border-b border-gray-100 last:border-b-0 ${danger ? 'text-red-600' : ''}`}
  >
    <div className="flex items-center gap-3">
      <div className={`p-2 rounded-lg ${danger ? 'bg-red-50' : 'bg-gray-100'}`}>
        <Icon className={`h-5 w-5 ${danger ? 'text-red-600' : 'text-gray-600'}`} />
      </div>
      <div className="text-left">
        <p className={`font-medium ${danger ? 'text-red-600' : 'text-gray-900'}`}>{label}</p>
        {description && <p className="text-xs text-gray-500 mt-0.5">{description}</p>}
      </div>
    </div>
    <div className="flex items-center gap-2">
      {badge && (
        <Badge variant={badgeVariant} className={`text-xs ${
          badgeVariant === 'success' ? 'bg-emerald-100 text-emerald-800' : 
          badgeVariant === 'warning' ? 'bg-amber-100 text-amber-800' : ''
        }`}>
          {badge}
        </Badge>
      )}
      {!danger && <ChevronRight className="h-5 w-5 text-gray-400" />}
    </div>
  </button>
);

// Section header component
const SectionHeader = ({ title, tone = 'default' }) => (
  <div className={`px-4 py-2 ${tone === 'danger' ? 'bg-red-50 border-b border-red-100' : 'bg-gray-50'}`}>
    <h3 className={`text-xs font-semibold uppercase tracking-wider ${tone === 'danger' ? 'text-red-700' : 'text-gray-500'}`}>
      {title}
    </h3>
  </div>
);

/**
 * AccountPage - A sectioned navigation layout for account settings
 * All chevrons navigate to dedicated pages - no inline expansions
 * 
 * @param {string} variant - 'vendor' or 'customer'
 * @param {function} onNavigate - Callback to switch tabs (for items within same dashboard)
 * @param {function} onLogout - Logout callback
 * @param {function} onFeedbackClick - Open feedback dialog
 * @param {object} profileVisibility - Vendor profile visibility state (vendor only)
 * @param {object} vendorProfile - Vendor profile data (vendor only)
 * @param {number} eventsCount - Number of event plans (customer only)
 */
const AccountPage = ({ 
  variant = 'customer',
  onNavigate,
  onLogout,
  onFeedbackClick,
  profileVisibility,
  vendorProfile,
  eventsCount = 0
}) => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const isVendor = variant === 'vendor';

  // Get visibility status badge for vendor
  const getVisibilityBadge = () => {
    if (profileVisibility?.admin_blocked) {
      return { text: 'Blocked', variant: 'warning' };
    }
    if (profileVisibility?.is_visible_to_customers) {
      return { text: 'Active', variant: 'success' };
    }
    return { text: 'Hidden', variant: 'secondary' };
  };

  return (
    <div className="space-y-4 pb-24">
      {/* Page Header */}
      <div className="px-1">
        <h1 className="text-2xl font-bold text-gray-900">My Account</h1>
        <p className="text-sm text-gray-500">Manage your profile and settings</p>
      </div>

      {/* Account Section */}
      <Card className="overflow-hidden">
        <SectionHeader title="Account" />
        <div className="divide-y divide-gray-100">
          <NavRow
            icon={User}
            label="Profile"
            description={isVendor ? "Edit your business profile" : "Update your personal info"}
            onClick={() => onNavigate?.(isVendor ? 'profile' : 'profile-edit')}
          />
          
          {isVendor && (
            <NavRow
              icon={profileVisibility?.is_visible_to_customers ? Eye : EyeOff}
              label="Profile & Visibility"
              description="Control who can find you"
              onClick={() => onNavigate?.('visibility-settings')}
              badge={getVisibilityBadge().text}
              badgeVariant={getVisibilityBadge().variant}
            />
          )}
          
          <NavRow
            icon={Shield}
            label="Account & Security"
            description="Email, phone, password, security questions"
            onClick={() => onNavigate?.(isVendor ? 'account-security' : 'profile-security')}
          />
          
          <NavRow
            icon={CreditCard}
            label="Payments"
            description={isVendor ? "Earnings & payout settings" : "History & receipts"}
            onClick={() => onNavigate?.(isVendor ? 'payments' : 'customer-payments')}
            data-testid="account-payments-nav"
          />
        </div>
      </Card>

      {/* My Events Section - Customer Only */}
      {!isVendor && (
        <Card className="overflow-hidden">
          <SectionHeader title="Planning" />
          <NavRow
            icon={PartyPopper}
            label="My Events"
            description="View and manage your event plans"
            onClick={() => onNavigate?.('events')}
            badge={eventsCount > 0 ? eventsCount.toString() : null}
            badgeVariant="success"
          />
        </Card>
      )}

      {/* Mode Section */}
      <Card className="overflow-hidden">
        <SectionHeader title="Mode" />
        <div className="p-4">
          <RoleSwitcher compact={true} />
        </div>
      </Card>

      {/* Notifications Section - Clickable to dedicated page */}
      <Card className="overflow-hidden">
        <SectionHeader title="Notifications" />
        <NavRow
          icon={Bell}
          label="Notification Settings"
          description="Email and push notification preferences"
          onClick={() => navigate('/settings/notifications')}
        />
      </Card>

      {/* Tools Section - Vendor Only */}
      {isVendor && (
        <Card className="overflow-hidden">
          <SectionHeader title="Tools" />
          <div className="divide-y divide-gray-100">
            <NavRow
              icon={Calendar}
              label="Calendar"
              description="Manage availability & booking rules"
              onClick={() => onNavigate?.('calendar')}
            />
            <NavRow
              icon={Lightbulb}
              label="Tips & Guides"
              description="Learn how to grow your business"
              onClick={() => onNavigate?.('tips')}
            />
            <NavRow
              icon={Gift}
              label="Referrals & Rewards"
              description="Earn rewards for referring providers"
              onClick={() => onNavigate?.('account-referrals')}
            />
            {vendorProfile?.id && (
              <NavRow
                icon={ExternalLink}
                label="View Public Profile"
                description="See how customers see you"
                onClick={() => navigate(`/vendors/${vendorProfile.id}`)}
              />
            )}
          </div>
        </Card>
      )}

      {/* Support Section */}
      <Card className="overflow-hidden">
        <SectionHeader title="Support" />
        <div className="divide-y divide-gray-100">
          <NavRow
            icon={HelpCircle}
            label="Help & Support"
            description="FAQs and contact information"
            onClick={() => onNavigate?.('help')}
          />
          <NavRow
            icon={MessageCircle}
            label="Send Feedback"
            description="Help us improve Celey"
            onClick={onFeedbackClick}
          />
        </div>
      </Card>

      {/* Legal Section — App Store Guideline 5.1.1 requires Privacy Policy
          and Terms to be reachable from within the app. Account page is the
          canonical surface for both. */}
      <Card className="overflow-hidden">
        <SectionHeader title="Legal" />
        <div className="divide-y divide-gray-100">
          <NavRow
            icon={ShieldCheck}
            label="Privacy Policy"
            description="How we handle your data"
            onClick={() => navigate('/privacy')}
            data-testid="account-privacy-row"
          />
          <NavRow
            icon={FileText}
            label="Terms of Service"
            description="Platform rules and agreement"
            onClick={() => navigate('/terms')}
            data-testid="account-terms-row"
          />
        </div>
      </Card>

      {/* TestFlight Diagnostics — developer-only menu entry. Hidden by
          default on production web and App Store / TestFlight release
          builds. Visible when (a) the build is in development mode
          (preview dev server / local `yarn start`) OR (b) the
          REACT_APP_ENABLE_DIAGNOSTICS_UI escape-hatch flag is explicitly
          set to "true" (e.g. via `.env.production.local` for a one-off
          debug archive). NODE_ENV is injected by CRA at build time and
          is not affected by the craco.config.js dotenv preload, so this
          gate is bullet-proof against env-merge surprises. The
          underlying /diagnostics route and utils/diagnostics.js
          telemetry remain reachable regardless. */}
      {(process.env.NODE_ENV !== 'production' || process.env.REACT_APP_ENABLE_DIAGNOSTICS_UI === 'true') && (
        <Card className="overflow-hidden border border-amber-200 bg-amber-50/30">
          <SectionHeader title="TestFlight" />
          <div>
            <NavRow
              icon={Activity}
              label="Diagnostics"
              description="Auth, resume, and API error trace (temporary)"
              onClick={() => navigate('/diagnostics')}
              data-testid="account-diagnostics-row"
            />
          </div>
        </Card>
      )}

      {/* System Section */}
      <Card className="overflow-hidden">
        <SectionHeader title="System" />
        <div>
          <NavRow
            icon={LogOut}
            label="Log Out"
            description={user?.email}
            onClick={onLogout}
            danger={true}
          />
        </div>
      </Card>

      {/* Danger Zone — destructive, irreversible actions */}
      <Card
        data-testid="account-danger-zone"
        className="overflow-hidden mt-6 border border-red-200 bg-red-50/40"
      >
        <SectionHeader title="Danger Zone" tone="danger" />
        <div>
          <NavRow
            icon={Trash2}
            label="Delete Account"
            description="Permanently remove your personal info"
            onClick={() => navigate('/account/delete')}
            danger={true}
            data-testid="account-delete-row"
          />
        </div>
      </Card>
    </div>
  );
};

export default AccountPage;

// ─── Native Emergency Reset (Feb 2026 hotfix) ─────────────────────────
// Surface for native iOS users when the normal Logout button hangs (e.g.,
// Capacitor Preferences IPC stall, axios interceptor deadlock). Performs
// a synchronous IPC-free local cleanup and hard-reloads the WebView so
// the next React mount starts from a clean logged-out state.
const NativeEmergencyResetSection = () => {
  const { forceLocalLogout } = useAuth();
  return (
    <Card data-testid="native-emergency-reset" className="overflow-hidden mt-4 border border-amber-200 bg-amber-50/40">
      <div className="px-4 pt-3 pb-1 text-xs font-semibold uppercase tracking-wider text-amber-700">
        Trouble signing out?
      </div>
      <div className="px-4 pb-3 text-xs text-amber-700/80">
        If the app seems stuck or won&apos;t log out, tap below to clear local session data and restart.
      </div>
      <div className="px-4 pb-4">
        <button
          type="button"
          onClick={() => {
            try { console.warn('[EMERGENCY-RESET] user invoked'); } catch (_) { /* noop */ }
            if (typeof forceLocalLogout === 'function') forceLocalLogout();
          }}
          className="w-full rounded-xl border border-amber-300 bg-white hover:bg-amber-100 text-amber-800 font-semibold py-2.5 text-sm transition-colors"
          data-testid="native-emergency-reset-btn"
        >
          Reset Session
        </button>
      </div>
    </Card>
  );
};
